import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import ApplyCapitalServiceForm from './ApplyCapitalServiceForm';
import PhoneInquiryNotice from './PhoneInquiryNotice';
import { createCapitalCounselServices } from '@/api/AdditionalServices/createAdditionalServices';
import apiManager from '@/api/AxiosInstance';
import { deleteInterestProducts, postInterestProducts, postProductInquiryCall } from '@/api/public';
import BasicButton from '@/components/Common/Button/BasicButton';
import PhoneCallFloationgButton from '@/components/Common/Button/PhoneCallFloationgButton';
import BaseModal from '@/components/Common/Modal/BaseModal';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import ProductInquiryQr from '@/components/Common/Popup/Contents/ProductInquiryQr';
import { ChatOutlineIcon, Cog6ToothIcon, PhoneRingOutlineIcon } from '@/components/Icon';
import CapitalGuideView from '@/components/More/CaptialCounselService/CapitalGuideView';
import { IS_ALREADY_APPLY_CAPITAL_COUNSEL_SERVICE } from '@/const/additionalService';
import { colors } from '@/const/colors';
import { ACTUAL_REPRESENTATIVE_PHONE_NUMBER, PRODUCT_DETAIL } from '@/const/common';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { useToastContext } from '@/contexts/Common/ToastContext';
import useAuthRedirect from '@/hooks/useAuthRedirect';
import { memberAtom, updateAdditionalServiceAtom } from '@/store/member';
import { phoneCall } from '@/utils/common';
import { getSalesPeopleCallNumber } from '@/utils/Products/productUtils';
import Favorite from '@mui/icons-material/Favorite';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import Checkbox from '@mui/material/Checkbox';

type ProductDetailProps = {
  product: ProductDetailResponse;
};

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

const ProductDetailFooter = ({ product }: ProductDetailProps) => {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authRedirect = useAuthRedirect();
  const { showToast } = useToastContext();

  const [memberAtomData] = useAtom(memberAtom);
  const isInterestProductOn = !!memberAtomData?.notificationSettings?.interestProduct;
  const [, updateAdditionalService] = useAtom(updateAdditionalServiceAtom);

  const [isNearBottom, setIsNearBottom] = useState(false);
  const threshold = 20;

  const [isAlreadyApplyCapitalService, setIsAlreadyApplyCaptialService] = useState(false);

  const [interestProductId, setInterestProductId] = useState<number | null>();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isConfirmModal, setIsConfirmModal] = useState(false);

  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);

  const [isMutating, setIsMutating] = useState(false);
  const [isOpenCapitalModal, setIsOpenCapitalModal] = useState(false);
  const [requestForm, setRequestForm] = useState<AdditionalServicesApplyForm>();

  const updateRequestForm = (formData: AdditionalServicesApplyForm) => {
    setRequestForm(formData);
  };

  const isDealerType = memberAtomData?.type ? memberAtomData.type?.code !== 'NORMAL' : false;
  const isDealerProduct = product.salesType?.code === 'THIRD_PARTY_DEALER';

  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    title: '',
    content: null,
    onClose: () => {},
  });

  const [isConfirmModalConfig, setIsConfirmModalConfig] = useState<ModalConfig>({
    title: '',
    content: null,
    leftButtonLabel: '',
    rightButtonLabel: '',
    onLeftButtonClick: () => {},
    onRightButtonClick: () => {},
  });

  useEffect(() => {
    if (product) {
      setInterestProductId(product?.interestedProductId || null);
    }
  }, [product]);

  useEffect(() => {
    if (memberAtomData) {
      setIsAlreadyApplyCaptialService(memberAtomData.isAlreadyApplyCapitalCounselService);
    }
  }, [memberAtomData]);

  const startPhoneCall = () => {
    if (!authRedirect(PRODUCT_DETAIL(product.id), { feature: '전화 문의' })) {
      return;
    }

    postProductInquiryCallMutation.mutate(product.id);

    if (isDealerType || isDealerProduct) {
      phoneCall(ACTUAL_REPRESENTATIVE_PHONE_NUMBER);
    } else {
      phoneCall(product.sellerSafetyNumber);
    }
  };

  const startChatting = () => {
    if (!authRedirect(PRODUCT_DETAIL(product.id), { feature: '채팅 문의' })) {
      return;
    }

    apiManager
      .post('/api/v1/chat-rooms', { productId: product.id })
      .then((response) => {
        if (response) {
          const responseData: ChattingDetailResponse = response.data;
          if (responseData.id) {
            navigate(`/chatting/room/${responseData.id}`);
          } else {
            navigate(`/chatting/room`, { state: { chatRoomData: responseData } });
          }
        }
      })
      .catch((error) => {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      });
  };

  const renderButton = () => {
    return (
      <BasicButton
        name="전화문의"
        img="https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/modal-phone.png"
        description={product.salesType?.code !== 'THIRD_PARTY_DEALER' ? <PhoneInquiryNotice /> : undefined}
        bgColor={colors.gray[0]}
        borderColor={colors.gray[4]}
        textColor={colors.gray[8]}
        fontSize={18}
        height={product.salesType?.code !== 'THIRD_PARTY_DEALER' ? 'auto' : 52}
        fontWeight="medium"
        onClick={startPhoneCall}
      ></BasicButton>
    );
  };

  const postInterestProductMutation = useMutation((productId: number) => postInterestProducts(productId), {
    onMutate: async () => {
      await queryClient.cancelQueries('productDetail');
      setIsMutating(true);
      setInterestProductId(0);
    },
    onSuccess: (response) => {
      if (response) {
        setInterestProductId(response.data);
      }
    },
    onError: () => {
      setInterestProductId(null);
      showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
    },
    onSettled: () => {
      setIsMutating(false);
    },
  });

  const createInterestProduct = () => {
    if (!authRedirect(PRODUCT_DETAIL(product.id), { feature: '찜하기' }) || isMutating) {
      return;
    }
    postInterestProductMutation.mutate(product.id);
  };

  const deleteInterestProductMutation = useMutation(
    (interestProductId: number) => deleteInterestProducts(interestProductId),
    {
      onMutate: async () => {
        await queryClient.cancelQueries('productDetail');
        setIsMutating(true);

        const prevData = interestProductId;

        setInterestProductId(null);
        return { prevData };
      },
      onError: (error, response, context) => {
        setInterestProductId(context?.prevData);
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      },
      onSettled: () => {
        setIsMutating(false);
      },
    },
  );

  const deleteInterestProduct = () => {
    if (isMutating) {
      return;
    }
    if (interestProductId) {
      deleteInterestProductMutation.mutate(interestProductId);
    }
  };

  const clickLikeButton = () => {
    if (!!interestProductId) {
      deleteInterestProduct();
    } else {
      setIsConfirmModal(true);
      setIsConfirmModalConfig({
        title: '찜한차량에 추가되었습니다!',
        content: (
          <>
            <div className="mb-4">관심 차량의 가격이 변경되면 직트럭 앱으로 알림을 보내드려요.</div>

            {!isInterestProductOn && (
              <span className="text-sm">
                * 알림 설정 : 더보기 &gt; 설정 (
                <span className="inline-block align-middle">
                  <Cog6ToothIcon color={colors.gray[9]} width="16px" height="16px" />
                </span>
                ) &gt; 알림설정 &gt; 관심차량 알림 on
              </span>
            )}
          </>
        ),
        leftButtonLabel: '닫기',
        rightButtonLabel: isInterestProductOn ? '' : '설정으로 이동',
        onLeftButtonClick: () => {
          setIsConfirmModal(false);
        },
        onRightButtonClick: isInterestProductOn
          ? undefined
          : () => {
              navigate('/setting');
            },
      });
      createInterestProduct();
    }
  };

  const onCloseModal = () => {
    setIsOpenModal(false);
  };

  const isNotDirectProduct = product.salesType?.code === 'ASSURANCE' || product.salesType?.code === 'CONSIGNMENT';

  const onClickChatInquiry = () => {
    if (isMobile) {
      startChatting();
    } else {
      setIsOpenModal(true);
      setModalConfig({
        title: '차량 구매문의',
        content: <ProductInquiryQr />,
        onClose: onCloseModal,
      });
    }
  };

  const onClickPhoneInquiry = () => {
    if (isMobile) {
      if (!authRedirect(PRODUCT_DETAIL(product.id), { feature: '전화 문의' })) {
        return;
      }
      setIsOpenModal(true);
      setModalConfig({
        title: `<span class="text-[18px] sm280:text-[16px] break-keep leading-[20px] sm280:leading-[18px] line-clamp-2">${product.truckName}</span>`,
        content: renderButton(),
        onClose: onCloseModal,
      });
    } else {
      setIsOpenModal(true);
      setModalConfig({
        title: '차량 구매문의',
        content: <ProductInquiryQr />,
        onClose: onCloseModal,
      });
    }
  };

  const onClickPhoneCall = () => {
    if (!authRedirect(PRODUCT_DETAIL(product.id), { feature: '전화 문의' })) {
      return;
    }

    if (isMobile) {
      const phoneNumberToCall =
        getSalesPeopleCallNumber(product.salesPeople) || ACTUAL_REPRESENTATIVE_PHONE_NUMBER;
      postProductInquiryCallMutation.mutate(product.id);
      phoneCall(phoneNumberToCall);
    } else {
      setIsOpenModal(true);
      setModalConfig({
        title: '차량 구매문의',
        content: <ProductInquiryQr />,
        onClose: onCloseModal,
      });
    }
  };

  const postProductInquiryCallMutation = useMutation((productId: number) => postProductInquiryCall(productId), {
    onSuccess: () => {},
    onError: () => {},
  });

  const postCapitalCounselServices = useMutation(
    (request: AdditionalServicesApplyRequest) => createCapitalCounselServices(request),
    {
      onSuccess: () => {
        showToast('화물차 대출 상담 서비스를 신청했어요.', 'success', 'bottom');
        setIsAlreadyApplyCaptialService(true);
        updateAdditionalService(IS_ALREADY_APPLY_CAPITAL_COUNSEL_SERVICE);
      },
      onError: (error: AxiosError) => {
        if (error.code && error.message) {
          showToast(error.message, 'error', 'bottom');
        } else {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        }
      },
      onSettled: () => {
        setIsOpenCapitalModal(false);
      },
    },
  );

  const handleClickApplyOk = () => {
    setIsOpenConfirmModal(false);
    if (isOpenCapitalModal) {
      const requestData = {
        name: memberAtomData?.name,
        phoneNumber: memberAtomData?.phoneNumber,
        productId: product.id,
      };

      postCapitalCounselServices.mutate(requestData);
    }
  };

  const handleClickApply = () => {
    if (!authRedirect(PRODUCT_DETAIL(product.id), { feature: '서비스 신청' })) {
      return;
    }

    setIsOpenConfirmModal(true);
  };

  const handleCloseModal = () => {
    setIsOpenCapitalModal(false);
    setIsNearBottom(false);
  };

  const handleScroll = () => {
    const popupElement = popupRef.current;
    const contentElement = contentRef.current;

    if (!popupElement) return;
    if (!contentElement) return;

    const scrollTop = popupElement.scrollTop;
    const viewportHeight = popupElement.clientHeight;
    const scrollHeight = contentElement.scrollHeight;

    setIsNearBottom(scrollTop + viewportHeight >= scrollHeight - threshold);
  };

  useEffect(() => {
    setTimeout(() => {
      handleScroll();
    }, 0);
  }, [isOpenCapitalModal]);

  const handleScrollBottom = () => {
    const popupElement = popupRef.current;
    const contentElement = contentRef.current;

    if (!popupElement) return;
    if (!contentElement) return;

    const scrollTop = popupElement.scrollTop;
    const viewportHeight = popupElement.clientHeight;
    const scrollHeight = contentElement.scrollHeight;

    if (scrollTop + viewportHeight < scrollHeight) {
      popupElement.scrollBy({
        top: viewportHeight,
        behavior: 'smooth',
      });
    } else {
      setIsNearBottom(true);
    }
  };

  return (
    <>
      <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-14 max-w-[720px] min-w-[280px] bg-white">
        <div className="absolute top-[-16px] left-0 w-full h-[16px] bg-gradient-to-b from-transparent to-[#ffffff]"></div>
        <div className="flex flex-col items-center justify-center mr-4">
          <Checkbox
            {...label}
            checked={!!interestProductId || false}
            icon={<FavoriteBorder />}
            sx={{
              color: '#919191',
              '&.Mui-checked': {
                color: '#FF4D4F',
              },
              padding: '0px',
              justifyContent: 'center',
            }}
            checkedIcon={<Favorite />}
            onClick={clickLikeButton}
          />
          <p className="text-xs text-center text-gray-6 md:text-sm sm:text-xs">찜하기</p>
        </div>
        <div className="flex items-center flex-1 my-2 text-base">
          <div className="flex w-full">
            {isNotDirectProduct ? (
              <div className="flex w-full gap-2">
                <BasicButton
                  name="대출상담 신청하기"
                  bgColor={colors.gray[0]}
                  borderColor={colors.gray[3]}
                  textColor={colors.gray[6]}
                  fontSize={16}
                  height={48}
                  fontWeight="bold"
                  onClick={() => setIsOpenCapitalModal(true)}
                />
                <BasicButton
                  name="전화 문의하기"
                  bgColor={colors.primary[10]}
                  borderColor={colors.primary[10]}
                  textColor={colors.gray[0]}
                  fontSize={16}
                  height={48}
                  fontWeight="bold"
                  onClick={onClickPhoneCall}
                />
              </div>
            ) : (
              <div className="flex w-full gap-2">
                {!isDealerProduct && (
                  <BasicButton
                    name="채팅 문의하기"
                    bgColor={colors.gray[0]}
                    borderColor={colors.primary[10]}
                    textColor={colors.primary[10]}
                    fontSize={16}
                    height={48}
                    fontWeight="bold"
                    onClick={onClickChatInquiry}
                    isDisabled={isDealerType}
                  />
                )}
                <BasicButton
                  name="전화 문의하기"
                  bgColor={colors.primary[10]}
                  borderColor={colors.primary[10]}
                  textColor={colors.gray[0]}
                  fontSize={16}
                  height={48}
                  fontWeight="bold"
                  onClick={onClickPhoneInquiry}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <BaseModal
        isOpen={isOpenModal}
        onClose={modalConfig.onClose}
        title={modalConfig.title}
        content={modalConfig.content}
        isXMarkVisible
        paddingType="padding-top-20"
      />
      <ConfirmModal
        isOpen={isConfirmModal}
        title={isConfirmModalConfig.title}
        content={isConfirmModalConfig.content}
        leftButtonLabel={isConfirmModalConfig.leftButtonLabel}
        rightButtonLabel={isConfirmModalConfig.rightButtonLabel}
        onLeftButtonClick={isConfirmModalConfig.onLeftButtonClick}
        onRightButtonClick={isConfirmModalConfig.onRightButtonClick}
      />
      <ConfirmModal
        isOpen={isOpenConfirmModal}
        title={`${product.truckName}`}
        content={
          <span>
            해당 차량으로 화물차 대출 상담
            <br />
            서비스를 신청할까요?
          </span>
        }
        onLeftButtonClick={() => {
          setIsOpenConfirmModal(false);
        }}
        rightButtonLabel="신청하기"
        onRightButtonClick={handleClickApplyOk}
      />

      <BottomSheetPopup
        ref={popupRef}
        isOpen={isOpenCapitalModal}
        onClose={handleCloseModal}
        title={isOpenCapitalModal ? '화물차 대출 상담 서비스' : ''}
        onScroll={handleScroll}
      >
        <div>
          {isOpenCapitalModal && (
            <>
              {/* <CapitalGuidePopup ref={contentRef} price={product?.price || 0} /> */}
              <div className="pb-[56px]">
                <CapitalGuideView ref={contentRef} price={product?.price || 0} isPopup />
              </div>

              <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-14 max-w-[720px] min-w-[280px] bg-white">
                <div className="absolute right-[16px] top-[-72px]">
                  <PhoneCallFloationgButton />
                </div>

                <div className="absolute top-[-16px] left-0 w-full h-[16px] bg-gradient-to-b from-transparent to-[#ffffff]"></div>
                <BasicButton
                  name={
                    isAlreadyApplyCapitalService
                      ? '한도 조회 신청완료'
                      : isNearBottom
                      ? '한도 조회 신청하기'
                      : '아래로 내리기'
                  }
                  bgColor={colors.primary[10]}
                  borderColor={colors.primary[10]}
                  textColor={colors.gray[0]}
                  fontSize={16}
                  height={48}
                  fontWeight={'bold'}
                  isDisabled={isAlreadyApplyCapitalService}
                  onClick={isNearBottom ? handleClickApply : handleScrollBottom}
                />
              </div>
            </>
          )}
        </div>
      </BottomSheetPopup>
    </>
  );
};

export default ProductDetailFooter;
